/* eslint-disable no-underscore-dangle */
import base64 from 'base-64';

import parseAamvaCode from 'apps/web_forms/utils/aamvaParser';
import { getQualifiedResourceId } from 'apps/web_forms/utils/resourcesUtils';
import { getIdWithoutSuffix } from 'apps/web_forms/utils/inputsUtils';

const resources = {};

/**
 * Add resource to scope of this file
 * Don't use directly, use via wrapper addResource in resourceSlice.js
 * @param {String} key
 * @param {Object} value
 */
export const __addResource = (key, value) => {
  resources[key] = value;
};

const getMatchingPrefix = (identifierA, identifierB) => {
  const maxIndex = Math.min(identifierA.length, identifierB.length);

  let matchUntil = 0;

  for (let i = 0; i < maxIndex; i++) {
    if (identifierA[i] === identifierB[i]) {
      matchUntil = i + 1;
    } else {
      break;
    }
  }

  return identifierA.substring(0, matchUntil);
};

export const fieldsShareSameParent = (fieldIdentifierA, fieldIdentifierB) => {
  const expectedMatchingPrefix = getMatchingPrefix(
    getIdWithoutSuffix(fieldIdentifierA),
    getIdWithoutSuffix(fieldIdentifierB)
  );

  const matchingPrefix = getMatchingPrefix(fieldIdentifierA, fieldIdentifierB);

  const fieldIsMatching =
    getIdWithoutSuffix(matchingPrefix) === expectedMatchingPrefix;

  return fieldIsMatching;
};

/**
 * LOOKUP function
 * @param {String} resourceId
 * @param {String} tableId
 * @param {String} keyColumn
 * @param {String} valueColumn
 * @param {String} keyValue
 * @param {number | 'latest'} resourceVersion Added and used internally, to determine which version of the resource to use for lookup
 */
export function getValueFromResourceLookup(
  resourceId,
  tableId,
  keyColumnId,
  valueColumnId,
  keyValue,
  resourceVersion
) {
  let parsedKeyValue = keyValue;

  if (typeof keyValue === 'string') {
    // Remove double quotes
    parsedKeyValue = `${keyValue}`.replace(/"/g, '');
  }
  if (Object.keys(resources).length > 0) {
    const resourceWithoutQuotes = resourceId.replace(/"/g, '');
    const qualifiedResourceId = getQualifiedResourceId(
      resourceWithoutQuotes,
      resourceVersion
    );

    const table = resources[qualifiedResourceId]?.find(
      (resource) => `"${resource.table_id}"` === tableId
    );

    if (!table) return null;

    const { valueCol, keyCol } = table.columns.reduce((valueKeyCol, column) => {
      const newValueKeyColumn = { ...valueKeyCol };

      if (`"${column.column_id}"` === valueColumnId) {
        newValueKeyColumn.valueCol = column;
      }

      if (`"${column.column_id}"` === keyColumnId) {
        newValueKeyColumn.keyCol = column;
      }

      return newValueKeyColumn;
    }, {});

    const keyRow = keyCol.rows?.find(({ row_value: rowValue }) => {
      if (!Number.isNaN(+rowValue)) {
        return +rowValue === parsedKeyValue;
      }
      return rowValue === parsedKeyValue;
    });

    if (!keyRow || keyRow.length === 0) {
      return null;
    }
    const result = valueCol.rows.filter(
      (eachRow) => eachRow.row_number === keyRow.row_number
    );

    if (result && result.length > 0) {
      return `"${result[0]?.row_value}"`;
    }
  }
  return null;
}

/**
 * GET function
 */
export function getFieldFromSource(source, field) {
  let cleanSource = `${source}`.replace(/"/g, '');

  if (cleanSource === '') return '';

  cleanSource = base64.decode(cleanSource);

  const cleanField = `${field}`.replace(/"/g, '');
  let response = '';

  try {
    const data = parseAamvaCode(cleanSource);

    switch (cleanField) {
      case 'first_name':
        response = data.name()?.first ?? '';
        break;
      case 'last_name':
        response = data.name()?.last ?? '';
        break;
      case 'license_number':
        response = data.id();
        break;
      case 'birth_date':
        response = data.birthday();
        break;
      case 'issuing_date':
        response = data.issuingDate;
        break;
      case 'expiry_date':
        response = data.expirationDate;
        break;

      default:
        // eslint-disable-next-line no-console
        console.log('🚨 Field not supported');
        response = '';
        break;
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log('🚨 error: ', error);
  }
  return response;
}
